import { useEffect, useMemo, useState } from 'react'
import { HiCheckCircle, HiXMark } from 'react-icons/hi2'
import { useAuth } from '@/context/AuthContext'
import { useWarehouse } from '@/context/WarehouseContext'
import {
  clearPendingWarehouseAuthSwitchNotice,
  getPendingWarehouseAuthSwitchNotice,
} from '@/features/warehouse/authSwitchNotice'

type NoticeState = {
  warehouseName: string
  userLabel: string
}

const getUserLabel = (user: {
  first_name?: string
  last_name?: string
  username?: string
  phone?: string
}): string => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()

  if (fullName) {
    return fullName
  }

  if (user.username) {
    return user.username
  }

  if (user.phone) {
    return user.phone
  }

  return 'пользователь'
}

const WarehouseAuthSwitchNotice = () => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const { selectedWarehouseId, selectedWarehouse } = useWarehouse()
  const [notice, setNotice] = useState<NoticeState | null>(null)

  useEffect(() => {
    if (isLoading) {
      return
    }

    const pendingNotice = getPendingWarehouseAuthSwitchNotice()

    if (!pendingNotice || pendingNotice.warehouseId !== selectedWarehouseId) {
      return
    }

    clearPendingWarehouseAuthSwitchNotice()

    if (!isAuthenticated || !user) {
      return
    }

    setNotice({
      warehouseName: pendingNotice.warehouseName || selectedWarehouse.name,
      userLabel: getUserLabel(user),
    })
  }, [isAuthenticated, isLoading, selectedWarehouse.name, selectedWarehouseId, user])

  useEffect(() => {
    if (!notice) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setNotice(null)
    }, 5000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [notice])

  const description = useMemo(() => {
    if (!notice) {
      return ''
    }

    return `Вы переключились на пользователя ${notice.userLabel} в магазине ${notice.warehouseName}.`
  }, [notice])

  if (!notice) {
    return null
  }

  return (
    <div className="pointer-events-none fixed right-4 top-22 z-[1200] w-[calc(100vw-2rem)] max-w-sm">
      <div className="pointer-events-auto rounded-2xl border border-emerald-200 bg-white shadow-[0_20px_50px_-24px_rgba(15,23,42,0.35)]">
        <div className="flex items-start gap-3 p-4">
          <div className="mt-0.5 shrink-0 text-emerald-500">
            <HiCheckCircle className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">Склад переключен</p>
            <p className="mt-1 text-sm leading-5 text-gray-600">{description}</p>
          </div>

          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Закрыть уведомление"
          >
            <HiXMark className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default WarehouseAuthSwitchNotice
